import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [logoPosition, setLogoPosition] = useState({ x: 100, y: 100 });
  const [logoVelocity, setLogoVelocity] = useState({ x: 2, y: 2 });
  const [logoColor, setLogoColor] = useState('#8B4513');
  const navigate = useNavigate();

  const coffeeColors = [
    '#8B4513', // SaddleBrown
    '#A0522D', // Sienna
    '#CD853F', // Peru
    '#D2691E', // Chocolate
    '#6F4E37', // Coffee
    '#8B7355', // Khaki
    '#795548', // Brown
    '#007bff', // Blue (agregado para consistencia)
    '#0056b3', // Dark Blue
    '#17a2b8'  // Teal Blue
  ];

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animación tipo DVD para el logo
  useEffect(() => {
    const logoSize = 60; // Tamaño del logo flotante
    const interval = setInterval(() => {
      setLogoPosition(prev => {
        let newX = prev.x + logoVelocity.x;
        let newY = prev.y + logoVelocity.y;
        let newVelocityX = logoVelocity.x;
        let newVelocityY = logoVelocity.y;
        let shouldChangeColor = false;

        // Rebote en los bordes horizontales
        if (newX <= 0 || newX >= windowSize.width - logoSize) {
          newVelocityX = -logoVelocity.x;
          newX = newX <= 0 ? 0 : windowSize.width - logoSize;
          shouldChangeColor = true;
        }

        // Rebote en los bordes verticales
        if (newY <= 0 || newY >= windowSize.height - logoSize) {
          newVelocityY = -logoVelocity.y;
          newY = newY <= 0 ? 0 : windowSize.height - logoSize;
          shouldChangeColor = true;
        }

        // Cambiar color al rebotar
        if (shouldChangeColor) {
          const randomColor = coffeeColors[Math.floor(Math.random() * coffeeColors.length)];
          setLogoColor(randomColor);
        }

        setLogoVelocity({ x: newVelocityX, y: newVelocityY });
        return { x: newX, y: newY };
      });
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [windowSize, logoVelocity, coffeeColors]);

  const isMobile = windowSize.width < 768;
  const isTablet = windowSize.width >= 768 && windowSize.width < 1024;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Buscar usuario en Firestore
      const usersRef = collection(db, 'vendedores');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Usuario no encontrado');
        return;
      }

      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();

      if (userData.password === password) {
        // Guardar información del usuario
        localStorage.setItem('user', JSON.stringify({
          id: userDoc.id,
          username: userData.username,
          rol: userData.rol
        }));

        // Redireccionar según el rol
        if (userData.rol === 'admin') {
          navigate('/admin');
        } else if (userData.rol === 'vendedor') {
          navigate('/vendedor');
        } else if (userData.rol === 'mesero') {
          navigate('/mesero');
        } else {
          navigate('/vendedor'); // Redirección por defecto
        }
      } else {
        setError('Contraseña incorrecta');
      }
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      setError('Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #FFFFFF 0%, #f8f9fa 30%, #e3f2fd 70%, #FFFFFF 100%)',
      padding: '20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Logo animado tipo DVD */}
      <img 
        src="/logo.png" 
        alt="Logo flotante"
        style={{
          position: 'absolute',
          left: logoPosition.x,
          top: logoPosition.y,
          width: '60px',
          height: '60px',
          objectFit: 'contain',
          borderRadius: '50%',
          filter: `drop-shadow(0 0 10px ${logoColor})`,
          transition: 'filter 0.3s ease',
          zIndex: 1,
          opacity: 0.7
        }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: isMobile ? '30px 20px' : '40px',
        borderRadius: '20px',
        boxShadow: '0 10px 30px rgba(0, 123, 255, 0.1), 0 5px 15px rgba(139, 69, 19, 0.1)',
        width: '100%',
        maxWidth: '400px',
        border: '1px solid rgba(0, 123, 255, 0.2)',
        position: 'relative',
        zIndex: 2
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          <img 
            src="/logo.png" 
            alt="Logo de la empresa"
            style={{
              width: isMobile ? '80px' : '100px',
              height: isMobile ? '80px' : '100px',
              objectFit: 'contain',
              marginBottom: '15px',
              borderRadius: '15px',
              boxShadow: '0 4px 15px rgba(139, 69, 19, 0.3)',
              border: '3px solid #D2B48C'
            }}
            onError={(e) => {
              // Fallback si no se puede cargar el logo
              e.target.style.display = 'none';
            }}
          />
          <h2 style={{ 
            background: 'linear-gradient(135deg, #007bff 0%, #8B4513 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: isMobile ? '20px' : '24px',
            margin: '0',
            fontWeight: '700',
            textShadow: 'none'
          }}>
            Kardhamo
          </h2>
          <p style={{
            color: '#007bff',
            fontSize: '14px',
            margin: '5px 0 0 0',
            fontWeight: '500'
          }}>
            Control de Inventario y Ventas
          </p>
        </div>
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ 
              background: 'linear-gradient(135deg, #007bff 0%, #8B4513 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Usuario:
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={{
                padding: '14px',
                border: '2px solid #D2B48C',
                borderRadius: '10px',
                fontSize: '16px',
                outline: 'none',
                transition: 'all 0.3s ease',
                backgroundColor: '#FEFCF8',
                color: '#8B4513'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#007bff';
                e.target.style.boxShadow = '0 0 10px rgba(0, 123, 255, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D2B48C';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ 
              background: 'linear-gradient(135deg, #007bff 0%, #8B4513 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Contraseña:
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                padding: '14px',
                border: '2px solid #D2B48C',
                borderRadius: '10px',
                fontSize: '16px',
                outline: 'none',
                transition: 'all 0.3s ease',
                backgroundColor: '#FEFCF8',
                color: '#8B4513'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#007bff';
                e.target.style.boxShadow = '0 0 10px rgba(0, 123, 255, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D2B48C';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          {loading && (
            <p style={{ 
              textAlign: 'center', 
              color: '#A0522D',
              margin: '10px 0',
              fontWeight: '500'
            }}>
              Cargando...
            </p>
          )}
          
          {error && (
            <p style={{ 
              color: '#8B4513',
              textAlign: 'center',
              margin: '10px 0',
              padding: '12px',
              backgroundColor: '#F5E6D3',
              borderRadius: '10px',
              fontSize: '14px',
              border: '2px solid #D2B48C',
              fontWeight: '500'
            }}>
              {error}
            </p>
          )}

          <button 
            type="submit" 
            disabled={loading}
            style={{
              backgroundColor: '#007bff',
              color: '#FFFFFF',
              padding: '16px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              opacity: loading ? 0.7 : 1,
              transform: loading ? 'scale(0.98)' : 'scale(1)',
              boxShadow: '0 6px 20px rgba(0, 123, 255, 0.3)',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'scale(1.02)';
                e.target.style.boxShadow = '0 8px 25px rgba(0, 123, 255, 0.4)';
                e.target.style.backgroundColor = '#0056b3';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 123, 255, 0.3)';
                e.target.style.backgroundColor = '#007bff';
              }
            }}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
